import { type FormEvent, useState } from 'react';
import {
  type ApiError,
  type CreateEndpointInput,
  createEndpoint,
  hasApiKey,
  toApiError,
} from '../../api/client';
import type { CreatedEndpoint } from '../../types';
import { CopyButton } from '../CopyButton';

export function RegisterEndpointForm({ onCreated }: { onCreated: () => void }) {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [eventTypes, setEventTypes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [created, setCreated] = useState<CreatedEndpoint | null>(null);

  if (!hasApiKey) return null;

  async function handleSubmit(submitEvent: FormEvent<HTMLFormElement>): Promise<void> {
    submitEvent.preventDefault();
    setError(null);
    setCreated(null);

    const input: CreateEndpointInput = { url: url.trim() };
    if (description.trim() !== '') input.description = description.trim();

    const types = eventTypes
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value !== '');
    if (types.length > 0) input.eventTypes = types;

    setSubmitting(true);
    try {
      const endpoint = await createEndpoint(input);
      setCreated(endpoint);
      setUrl('');
      setDescription('');
      setEventTypes('');
      onCreated();
    } catch (requestError) {
      setError(toApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={(submitEvent) => void handleSubmit(submitEvent)}>
      <div className="form__row">
        <div className="field">
          <label className="field__label" htmlFor="endpoint-url">
            Subscriber URL <span className="field__required">required</span>
          </label>
          <input
            id="endpoint-url"
            className="input mono"
            type="url"
            value={url}
            onChange={(changeEvent) => setUrl(changeEvent.target.value)}
            placeholder="http://localhost:4001/hook"
            required
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="endpoint-types">
            Event types <span className="field__optional">optional</span>
          </label>
          <input
            id="endpoint-types"
            className="input mono"
            value={eventTypes}
            onChange={(changeEvent) => setEventTypes(changeEvent.target.value)}
            placeholder="comma separated; blank subscribes to everything"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="endpoint-description">
          Description <span className="field__optional">optional</span>
        </label>
        <input
          id="endpoint-description"
          className="input"
          value={description}
          onChange={(changeEvent) => setDescription(changeEvent.target.value)}
          placeholder="local simulator"
          autoComplete="off"
        />
      </div>

      <div className="form__actions">
        <button type="submit" className="btn" disabled={submitting || url.trim() === ''}>
          {submitting ? 'Registering…' : 'Register endpoint'}
        </button>
      </div>

      {error ? (
        <p className="notice notice--danger" role="alert">
          {error.message}
          {error.detail ? <span className="mono"> — {error.detail}</span> : null}
        </p>
      ) : null}

      {created ? (
        <div className="notice notice--secret" role="status">
          <p className="notice__title">Signing secret — shown once</p>
          <p>
            The API never returns this again. Copy it now if the subscriber needs to verify
            signatures.
          </p>
          <div className="secret">
            <code className="secret__value mono">{created.secret}</code>
            <CopyButton value={created.secret} label="Copy secret" />
          </div>
          <p className="notice__meta mono">
            {created.id} · {created.url}
          </p>
        </div>
      ) : null}
    </form>
  );
}
